// Custom DS-Table column rendering the Quote Status: a design-system Pill per quote status, plain
// "No quote" text when the project has none (the public column API has no arbitrary-JSX cell, so we
// author a column following the established DS pattern — see StatusPillColumn — and import the two
// cell/column-support helpers from the dist internals, the accepted web-front convention).
//
// The pills are outlined rather than filled because the filled palette has no error colour: Pill's
// `variant` covers highlight/success/neutral/information only, and red lives solely on
// `outlinedVariant`. A declined quote has to read as an error, so every status here uses the
// outlined form to keep one visual style across the column.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import Styled from 'styled-components';
import {Button, EMPHASIS, Pill, OutlinedPillVariant} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import type {CreateQuoteTarget} from '../opportunityQuoteActions';

export interface QuoteStatusColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	/** Localized text for a project without a quote. */
	noQuoteLabel: string;
	width?: string;
	// px: the default width (and what a grip double-click resets to) and the drag lower bound.
	size?: number;
	minSize?: number;
	/** Create Quote control (spec 010 �3). All three must be supplied for it to render. */
	createQuoteLabel?: string;
	createQuoteTarget?: (row: OpportunityTableRow) => CreateQuoteTarget;
	onCreateQuote?: (row: OpportunityTableRow, target: Extract<CreateQuoteTarget, {show: true}>) => void;
}

// The cell reads "No quote" and the Create Quote control takes its place on row hover or keyboard focus, and is
// always shown on touch devices, which have no hover (D20). Both sit in one grid cell, so the slot is only as wide
// as the button - side by side they overflowed the column at any width (D11) - and visibility is CSS only, so the
// control stays mounted and focusable.
const CreateQuoteSlot = Styled.span`
	display: inline-grid;
	align-items: center;
	justify-items: center;
	& > * {
		grid-area: 1 / 1;
	}
	.create-quote-control {
		visibility: hidden;
	}
	tr:hover &,
	tr:focus-within &,
	&:focus-within {
		.no-quote-label {
			visibility: hidden;
		}
		.create-quote-control {
			visibility: visible;
		}
	}
	@media (hover: none) {
		.no-quote-label {
			visibility: hidden;
		}
		.create-quote-control {
			visibility: visible;
		}
	}
`;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuoteStatusColumn = (_: QuoteStatusColumnType): null => null;

/**
 * The outlined pill variant for a quote status. Undefined means the cell falls back to plain text.
 *
 * All five statuses a quote can hold are mapped (002 D7): without Sent and Revising, a sent quote falls
 * through to the no-quote text and sorts into the wrong group. The label itself comes from the row
 * (`quoteStatusLabel`, resolved once in opportunityTableRows against QUOTE_STATUS_MESSAGE_IDS) so the
 * text a user reads and the text D7 sorts on can never diverge — a status missing from both maps
 * therefore renders, and sorts, as "no quote".
 */
const VARIANT_FOR_STATUS: Record<string, OutlinedPillVariant> = {
	PUBLISHED: OutlinedPillVariant.NEUTRAL,
	// Sent and Revising are both "in flight with the client" — the active outline, distinct from the
	// terminal success/error pair and from a merely published draft.
	SENT: OutlinedPillVariant.ACTIVE,
	REVISING: OutlinedPillVariant.ACTIVE,
	APPROVED: OutlinedPillVariant.SUCCESS,
	DECLINED: OutlinedPillVariant.ERROR,
};

QuoteStatusColumn.toColumnDef = (child: React.ReactElement<QuoteStatusColumnType>): ColumnDef<OpportunityTableRow> => {
	// "No quote", plus the Create Quote control when this row's target says to offer one (D13-D15).
	const noQuote = (original: OpportunityTableRow): React.ReactNode => {
		const target = child.props.createQuoteTarget?.(original);
		const onCreate = child.props.onCreateQuote;
		if (!target?.show || !onCreate) return child.props.noQuoteLabel;
		return (
			<CreateQuoteSlot>
				<span className="no-quote-label">{child.props.noQuoteLabel}</span>
				{/* The wrapper stops the click reaching the row (D16) even if the DS Button lets the native event through. */}
				<span className="create-quote-control" onClick={e => e.stopPropagation()}>
					<Button
						icon="plus"
						emphasis={EMPHASIS.LOW}
						data-cy="create-quote-button"
						onPress={() => onCreate(original, target)}
					>
						{child.props.createQuoteLabel}
					</Button>
				</span>
			</CreateQuoteSlot>
		);
	};
	return {
		...defaultColumnDef(child.props),
		accessorKey: child.props.accessorKey,
		sortingFn: child.props.sortingFn,
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const {quoteStatus, quoteStatusLabel} = row.original;
			const variant = quoteStatus ? VARIANT_FOR_STATUS[quoteStatus] : undefined;
			return (
				<StyledCell align={child.props.align} data-cy="opportunity-quote-status-cell">
					{variant && quoteStatusLabel ? (
						<span data-cy="quote-status-pill">
							<Pill outlined outlinedVariant={variant}>
								{quoteStatusLabel}
							</Pill>
						</span>
					) : (
						noQuote(row.original)
					)}
				</StyledCell>
			);
		},
	};
};

export default QuoteStatusColumn;
